/**
 * DWARF-guided stack-slot promotion.
 *
 * At low optimization a compiler keeps source locals in the stack frame, accessed as
 * `memory[frameBase + offset]`, and DWARF records each one with `DW_OP_fbreg offset` (a byte
 * offset from the frame base). Given that offset→name map (see DwarfNaming.frameLocalsFor),
 * this pass promotes each named frame slot to a scalar local: a load becomes the variable, a
 * store an assignment to it. That collapses the prologue's parameter spills (`memory[fp+28] =
 * count`, where slot 28 is the parameter `count`, into a self-copy that dead-code elimination
 * removes) and turns the body into readable named statements.
 *
 * The frame base itself is found by data flow — the `__stack_pointer ± constant` value and its
 * copies — not by the DWARF frame-base local: at -O0 that local is named per SSA version by the
 * usage heuristic (`ptr`), so a local-index→name lookup is unreliable. DWARF's DW_OP_fbreg
 * offsets are relative to this same base, so the byte offsets line up directly.
 *
 * Conservative: a slot is promoted only when it is accessed solely as a scalar load/store of a
 * single consistent width, with no overlapping sub-access and its address never escaping — so
 * arrays, address-taken locals, and unions are left as raw frame memory.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';
import { eliminateDeadAssigns, cleanupLowered } from './LoweredTransforms';
import { collectReferencedVars } from './LoweredEmitter';

/**
 * A function's stack-frame locals from DWARF: the wasm local that holds the frame base, and
 * the name of each frame byte-offset a parameter or variable lives at (DW_OP_fbreg). Lets the
 * decompiler promote `memory[frameBase + offset]` accesses to the named scalar local.
 */
export interface FrameLocals {
  frameBaseLocal: number;
  slotNames: Map<number, string>;
}

export function promoteFrameLocals(node: LoweredNode, slotNames: Map<number, string>): LoweredNode {
  const aliases = computeFrameBaseAliases(node);
  if (aliases.size === 0) {
    return node;
  }
  const accesses: FrameAccess[] = [];
  collectFrameAccesses(node, aliases, accesses);
  const promotable = selectPromotableOffsets(slotNames, accesses);
  if (promotable.size === 0) {
    return node;
  }
  const rewritten = new FramePromoter(aliases, slotNames, promotable).rewrite(node);
  // Promotion turns the parameter spills into self-copies and leaves the frame-base
  // computation and its alias copies with no readers. eliminateDeadAssigns drops the
  // self-copies; the whole-function sweep then removes the now-unread base chain.
  return cleanupLowered(removeUnreadPureAssignments(eliminateDeadAssigns(rewritten)));
}

// The frame base and every variable that holds a copy of it, found by data flow rather than by
// name: the heuristic that names the base (`ptr`) keys on a single SSA version, so a name lookup
// is unreliable. A frame base is `stackPointer ± constant`; the stack pointer is the
// `__stack_pointer` global (read into a copy at -O0), and the constant may be a literal or a
// constant-valued variable (the `var3 = 32; base = sp - var3` shape). DWARF's DW_OP_fbreg
// offsets are relative to this same base, so the byte offsets line up directly.
function computeFrameBaseAliases(node: LoweredNode): Set<string> {
  const assignments = collectAssignmentValues(node);
  const stackPointerCopies = closeOver(assignments, (value, members) =>
    isStackPointerGlobal(value) || isCopyOf(value, members));
  const constants = closeOver(assignments, (value, members) =>
    value.kind === 'const' || isCopyOf(value, members));
  return closeOver(assignments, (value, bases) =>
    isFrameBaseSeed(value, stackPointerCopies, constants) || isCopyOf(value, bases));
}

// Iteratively adds every target all of whose assigned values satisfy `accept` (given the set
// built so far), to a fixpoint — the standard closure used to classify copy/derivation chains.
function closeOver(assignments: Map<string, Expression[]>, accept: (value: Expression, members: Set<string>) => boolean): Set<string> {
  const members = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const [target, values] of assignments) {
      if (!members.has(target) && values.length > 0 && values.every(value => accept(value, members))) {
        members.add(target);
        changed = true;
      }
    }
  }
  return members;
}

function isCopyOf(value: Expression, members: Set<string>): boolean {
  return value.kind === 'var' && members.has(value.name);
}

function isStackPointerGlobal(value: Expression): boolean {
  return value.kind === 'global' && /stack_pointer/i.test(value.name);
}

// A frame-base allocation: the stack pointer (or a copy of it) plus or minus a constant. The
// non-pointer operand may be a constant literal or a constant-valued variable.
function isFrameBaseSeed(value: Expression, stackPointerCopies: Set<string>, constants: Set<string>): boolean {
  if (value.kind !== 'binary' || (value.op !== '+' && value.op !== '-')) {
    return false;
  }
  const leftIsPointer = value.left.kind === 'var' && stackPointerCopies.has(value.left.name);
  const rightIsConstant = value.right.kind === 'const' || (value.right.kind === 'var' && constants.has(value.right.name));
  if (leftIsPointer && rightIsConstant) {
    return true;
  }
  const rightIsPointer = value.right.kind === 'var' && stackPointerCopies.has(value.right.name);
  const leftIsConstant = value.left.kind === 'const' || (value.left.kind === 'var' && constants.has(value.left.name));
  return value.op === '+' && rightIsPointer && leftIsConstant;
}

// Every value assigned to each variable, keyed by target, across the whole function. A variable
// is classified only when all of its assignments agree, so a reused slot disqualifies itself.
function collectAssignmentValues(node: LoweredNode): Map<string, Expression[]> {
  const byTarget = new Map<string, Expression[]>();
  walkLoweredTree(
    node,
    statement => {
      if (statement.kind === 'assign') {
        const values = byTarget.get(statement.target) ?? [];
        values.push(statement.value);
        byTarget.set(statement.target, values);
      }
    },
    () => {},
  );
  return byTarget;
}

// ─── dead frame-base cleanup ───

// Whole-function removal of assignments whose target is read nowhere and whose value has no
// side effect, iterated to a fixpoint: dropping `base = sp - n` then makes the feeders `sp`
// and `n` dead in turn. After promotion the only such debris is the frame-base computation
// and its alias copies, whose sole readers were the slot accesses promotion just rewrote.
function removeUnreadPureAssignments(node: LoweredNode): LoweredNode {
  let current = node;
  for (;;) {
    const referenced = new Set<string>();
    collectReferencedVars(current, referenced);
    const counter = { removed: 0 };
    current = dropUnreadAssignments(current, referenced, counter);
    if (counter.removed === 0) {
      return current;
    }
  }
}

function dropUnreadAssignments(node: LoweredNode, referenced: Set<string>, counter: { removed: number }): LoweredNode {
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(child => dropUnreadAssignments(child, referenced, counter)) };
    case 'block': return { kind: 'block', body: node.body.filter(statement => keepStatement(statement, referenced, counter)) };
    case 'if': return { kind: 'if', condition: node.condition, thenBody: dropUnreadAssignments(node.thenBody, referenced, counter), elseBody: node.elseBody ? dropUnreadAssignments(node.elseBody, referenced, counter) : null };
    case 'while': return { kind: 'while', condition: node.condition, body: dropUnreadAssignments(node.body, referenced, counter) };
    case 'do_while': return { kind: 'do_while', body: dropUnreadAssignments(node.body, referenced, counter), condition: node.condition };
    // A for-loop's init/increment are required slots, not free statements, so they are kept.
    case 'for': return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: dropUnreadAssignments(node.body, referenced, counter) };
    case 'switch': return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: dropUnreadAssignments(caseEntry.body, referenced, counter) })), defaultBody: dropUnreadAssignments(node.defaultBody, referenced, counter) };
    case 'labeled_block': return { kind: 'labeled_block', label: node.label, body: dropUnreadAssignments(node.body, referenced, counter) };
    default: return node;
  }
}

function keepStatement(statement: Statement, referenced: Set<string>, counter: { removed: number }): boolean {
  if (statement.kind === 'assign' && !referenced.has(statement.target) && !expressionMayHaveSideEffect(statement.value)) {
    counter.removed++;
    return false;
  }
  return true;
}

function expressionMayHaveSideEffect(expr: Expression): boolean {
  if (expr.kind === 'call' || expr.kind === 'call_indirect') {
    return true;
  }
  return childExpressions(expr).some(child => expressionMayHaveSideEffect(child));
}

interface FrameAccess {
  offset: number;
  width: number;
  // True when a frame-relative address is used outside a plain load/store (taken, passed,
  // computed) — such a slot can't be a simple scalar and is left as raw frame memory.
  escaping: boolean;
}

// The byte offset addressed relative to a frame-base alias, or null when `expr` is neither
// `alias` (offset 0) nor `alias + constant`.
function frameRelativeOffset(expr: Expression, aliases: Set<string>): number | null {
  if (expr.kind === 'var' && aliases.has(expr.name)) {
    return 0;
  }
  if (expr.kind === 'binary' && expr.op === '+') {
    if (expr.left.kind === 'var' && aliases.has(expr.left.name) && expr.right.kind === 'const' && typeof expr.right.value === 'number') {
      return expr.right.value;
    }
    if (expr.right.kind === 'var' && aliases.has(expr.right.name) && expr.left.kind === 'const' && typeof expr.left.value === 'number') {
      return expr.left.value;
    }
  }
  return null;
}

function accessWidth(memoryType: string): number {
  if (memoryType.includes('8')) { return 1; }
  if (memoryType.includes('16')) { return 2; }
  if (memoryType.includes('32')) { return 4; }
  return memoryType.startsWith('i64') || memoryType.startsWith('f64') ? 8 : 4;
}

function scalarTypeOf(memoryType: string): string {
  if (memoryType.startsWith('f64')) { return 'f64'; }
  if (memoryType.startsWith('f32')) { return 'f32'; }
  if (memoryType.startsWith('i64')) { return 'i64'; }
  return 'i32';
}

// ─── access collection ───

function collectFrameAccesses(node: LoweredNode, aliases: Set<string>, into: FrameAccess[]): void {
  const visitExpression = (expr: Expression): void => {
    // A frame-relative value used anywhere but as a load/store address means the slot's own
    // address is taken; record the escape and stop, since the whole address has left.
    const escapingOffset = frameRelativeOffset(expr, aliases);
    if (escapingOffset !== null) {
      into.push({ offset: escapingOffset, width: 0, escaping: true });
      return;
    }
    if (expr.kind === 'load') {
      const loadOffset = frameRelativeOffset(expr.address, aliases);
      if (loadOffset !== null) {
        into.push({ offset: loadOffset + expr.offset, width: accessWidth(expr.loadType), escaping: false });
        return;
      }
    }
    for (const child of childExpressions(expr)) {
      visitExpression(child);
    }
  };
  const visitStatement = (statement: Statement): void => {
    if (statement.kind === 'store') {
      const storeOffset = frameRelativeOffset(statement.address, aliases);
      if (storeOffset !== null) {
        into.push({ offset: storeOffset + statement.offset, width: accessWidth(statement.storeType), escaping: false });
        visitExpression(statement.value);
        return;
      }
    }
    // An alias-to-alias copy is closure plumbing, not an escape of the frame address.
    if (statement.kind === 'assign' && aliases.has(statement.target) && statement.value.kind === 'var' && aliases.has(statement.value.name)) {
      return;
    }
    for (const expr of statementExpressions(statement)) {
      visitExpression(expr);
    }
  };
  walkLoweredTree(node, visitStatement, visitExpression);
}

// A slot offset is promotable when every access to it is a load/store of one consistent scalar
// width, nothing escapes its address, and no other access lands inside [offset, offset+width).
function selectPromotableOffsets(slotNames: Map<number, string>, accesses: FrameAccess[]): Set<number> {
  const promotable = new Set<number>();
  for (const [offset] of slotNames) {
    const atOffset = accesses.filter(access => access.offset === offset);
    if (atOffset.length === 0 || atOffset.some(access => access.escaping)) {
      continue;
    }
    const width = atOffset[0].width;
    if (width === 0 || atOffset.some(access => access.width !== width)) {
      continue;
    }
    if (!accesses.some(access => access.offset > offset && access.offset < offset + width)) {
      promotable.add(offset);
    }
  }
  return promotable;
}

/** Rewrites promotable frame-slot loads/stores into reads/writes of the named scalar local. */
class FramePromoter {
  constructor(
    private readonly aliases: Set<string>,
    private readonly slotNames: Map<number, string>,
    private readonly promotable: Set<number>,
  ) {}

  rewrite(node: LoweredNode): LoweredNode {
    return this.mapNode(node);
  }

  // The scalar name for a frame-relative load/store address at the given memarg offset, or null
  // when the addressed slot is not a promotable named slot.
  private slotName(address: Expression, memargOffset: number): string | null {
    const base = frameRelativeOffset(address, this.aliases);
    if (base === null) {
      return null;
    }
    const offset = base + memargOffset;
    return this.promotable.has(offset) ? this.slotNames.get(offset) ?? null : null;
  }

  private mapExpression(expr: Expression): Expression {
    if (expr.kind === 'load') {
      const name = this.slotName(expr.address, expr.offset);
      if (name !== null) {
        return { kind: 'var', name, type: scalarTypeOf(expr.loadType) };
      }
      return { kind: 'load', address: this.mapExpression(expr.address), offset: expr.offset, loadType: expr.loadType };
    }
    return rebuildExpression(expr, child => this.mapExpression(child));
  }

  private mapStatement(statement: Statement): Statement {
    if (statement.kind === 'store') {
      const name = this.slotName(statement.address, statement.offset);
      if (name !== null) {
        return { kind: 'assign', target: name, type: scalarTypeOf(statement.storeType), value: this.mapExpression(statement.value) };
      }
    }
    return rebuildStatement(statement, expr => this.mapExpression(expr));
  }

  private mapNode(node: LoweredNode): LoweredNode {
    return rebuildNode(node, statement => this.mapStatement(statement), expr => this.mapExpression(expr), child => this.mapNode(child));
  }
}

// ─── generic lowered-tree walks (shared shape with the other lowered passes) ───

function childExpressions(expr: Expression): Expression[] {
  switch (expr.kind) {
    case 'binary': case 'compare': return [expr.left, expr.right];
    case 'unary': case 'convert': return [expr.operand];
    case 'load': return [expr.address];
    case 'select': return [expr.condition, expr.trueVal, expr.falseVal];
    case 'call': return expr.args;
    case 'call_indirect': return [expr.tableIndex, ...expr.args];
    case 'field_access': return [expr.base];
    case 'frame_slot': return [expr.index];
    default: return [];
  }
}

function statementExpressions(statement: Statement): Expression[] {
  switch (statement.kind) {
    case 'assign': return [statement.value];
    case 'store': return [statement.address, statement.value];
    case 'call': return statement.args;
    case 'call_indirect': return [statement.tableIndex, ...statement.args];
    case 'global_set': return [statement.value];
    case 'return': return statement.values;
    case 'expr': return [statement.value];
    default: return [];
  }
}

// Visits every statement and every node-level expression — loop/if conditions, switch
// selectors, return values — so access collection sees frame use in conditions, not only in
// block statements (a frame address escaping through a loop condition must still be caught).
function walkLoweredTree(node: LoweredNode, visitStatement: (statement: Statement) => void, visitExpression: (expr: Expression) => void): void {
  switch (node.kind) {
    case 'sequence': node.children.forEach(child => walkLoweredTree(child, visitStatement, visitExpression)); break;
    case 'block': node.body.forEach(visitStatement); break;
    case 'if': visitExpression(node.condition); walkLoweredTree(node.thenBody, visitStatement, visitExpression); if (node.elseBody) { walkLoweredTree(node.elseBody, visitStatement, visitExpression); } break;
    case 'while': if (node.condition) { visitExpression(node.condition); } walkLoweredTree(node.body, visitStatement, visitExpression); break;
    case 'do_while': walkLoweredTree(node.body, visitStatement, visitExpression); visitExpression(node.condition); break;
    case 'for': visitStatement(node.init); visitExpression(node.condition); visitStatement(node.increment); walkLoweredTree(node.body, visitStatement, visitExpression); break;
    case 'switch': visitExpression(node.selector); node.cases.forEach(caseEntry => walkLoweredTree(caseEntry.body, visitStatement, visitExpression)); walkLoweredTree(node.defaultBody, visitStatement, visitExpression); break;
    case 'labeled_block': walkLoweredTree(node.body, visitStatement, visitExpression); break;
    case 'return': node.values.forEach(visitExpression); break;
  }
}

function rebuildExpression(expr: Expression, map: (child: Expression) => Expression): Expression {
  switch (expr.kind) {
    case 'binary': return { kind: 'binary', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'compare': return { kind: 'compare', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'unary': return { kind: 'unary', op: expr.op, operand: map(expr.operand) };
    case 'convert': return { kind: 'convert', op: expr.op, operand: map(expr.operand) };
    case 'select': return { kind: 'select', condition: map(expr.condition), trueVal: map(expr.trueVal), falseVal: map(expr.falseVal) };
    case 'load': return { kind: 'load', address: map(expr.address), offset: expr.offset, loadType: expr.loadType };
    case 'call': return { kind: 'call', name: expr.name, args: expr.args.map(map) };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: map(expr.tableIndex), args: expr.args.map(map) };
    case 'field_access': return { kind: 'field_access', base: map(expr.base), offset: expr.offset };
    case 'frame_slot': return { kind: 'frame_slot', index: map(expr.index) };
    default: return expr;
  }
}

function rebuildStatement(statement: Statement, map: (expr: Expression) => Expression): Statement {
  switch (statement.kind) {
    case 'assign': return { kind: 'assign', target: statement.target, type: statement.type, value: map(statement.value) };
    case 'store': return { kind: 'store', address: map(statement.address), offset: statement.offset, storeType: statement.storeType, value: map(statement.value) };
    case 'call': return { kind: 'call', name: statement.name, args: statement.args.map(map), result: statement.result };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: map(statement.tableIndex), args: statement.args.map(map), result: statement.result };
    case 'global_set': return { kind: 'global_set', name: statement.name, value: map(statement.value) };
    case 'return': return { kind: 'return', values: statement.values.map(map) };
    case 'expr': return { kind: 'expr', value: map(statement.value) };
    default: return statement;
  }
}

function rebuildNode(
  node: LoweredNode,
  mapStatement: (statement: Statement) => Statement,
  mapExpression: (expr: Expression) => Expression,
  recurse: (child: LoweredNode) => LoweredNode,
): LoweredNode {
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(recurse) };
    case 'block': return { kind: 'block', body: node.body.map(mapStatement) };
    case 'if': return { kind: 'if', condition: mapExpression(node.condition), thenBody: recurse(node.thenBody), elseBody: node.elseBody ? recurse(node.elseBody) : null };
    case 'while': return { kind: 'while', condition: node.condition ? mapExpression(node.condition) : null, body: recurse(node.body) };
    case 'do_while': return { kind: 'do_while', body: recurse(node.body), condition: mapExpression(node.condition) };
    case 'for': return { kind: 'for', init: mapStatement(node.init), condition: mapExpression(node.condition), increment: mapStatement(node.increment), body: recurse(node.body) };
    case 'switch': return { kind: 'switch', selector: mapExpression(node.selector), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: recurse(caseEntry.body) })), defaultBody: recurse(node.defaultBody) };
    case 'labeled_block': return { kind: 'labeled_block', label: node.label, body: recurse(node.body) };
    case 'return': return { kind: 'return', values: node.values.map(mapExpression) };
    default: return node;
  }
}
